import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const LaborCostTracking = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Wrench" className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Seguimiento de Costos de Mano de Obra
          </h3>
        </div>
        <div className="flex justify-center items-center h-64">
          <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  const { totalLaborCost = 0, byTechnician = [] } = data || {};

  const chartData = byTechnician?.slice(0, 8)?.map((item, index) => ({
    name: item?.technician?.length > 15 ? item?.technician?.substring(0, 15) + '...' : item?.technician,
    fullName: item?.technician,
    value: item?.cost,
    fill: COLORS?.[index % COLORS?.length]
  }));

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(value);
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Wrench" className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Seguimiento de Costos de Mano de Obra
          </h3>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(totalLaborCost)}
          </p>
        </div>
      </div>

      {byTechnician?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Icon name="Wrench" className="w-12 h-12 mb-3 opacity-50" />
          <p className="text-sm">No hay datos de mano de obra disponibles</p>
        </div>
      ) : (
        <>
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => {
                    const item = chartData?.find(d => d?.name === label);
                    return item?.fullName || label;
                  }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {chartData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground mb-3">Top Técnicos</p>
            {byTechnician?.slice(0, 5)?.map((item, index) => {
              const percentage = totalLaborCost > 0 ? (item?.cost / totalLaborCost) * 100 : 0;
              return (
                <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                    />
                    <span className="text-sm text-foreground">{item?.technician}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">
                      {formatCurrency(item?.cost)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {percentage?.toFixed(1)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default LaborCostTracking;